import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AxiosResponse } from 'axios';

import {
  updateRealtyOffersPrimary,
  clearFiltersPrimary,
} from './redux/offersPrimary.actions';

import {
  handleInputsIntervals,
  handlePriceInputs,
  validateForm,
} from './redux/offersPrimary.controller';

import {
  setDisplayCharts,
  updateClusters,
  updateMarkers,
} from '../redux/dashboardActions';

import { NameIdPair, PrimaryMarketGeneralInputs } from '../types';

import {
  Button,
  Input,
  IResponse,
  Select,
  MenuWrapper,
  Spinner,
} from '../../../component';

import './realtyOffers.scss';
import {
  setMaxDate,
  setMaxUsefulArea,
  setMinDate,
  setMinMaxAreaLocalFilters,
  setMinMaxDateLocalFilters,
  setMinUsefulArea,
} from '../charts/redux/charts.actions';
import { getAvaliableFilters } from './redux/offersPrimary.infra';

type RealtyOffersPrimaryProps = {
  closeModalAction?: () => void;
};

const RealtyOffersPrimaryGeneral: React.FC<RealtyOffersPrimaryProps> = ({
  closeModalAction,
}: RealtyOffersPrimaryProps) => {
  const dispatch = useDispatch();

  const { elastic_city_id } = useSelector(
    (state: any) => state.mapSearch.selectedCity || {}
  );

  const { moreFilters } = useSelector((state: any) => state.moreFiltersState);
  const { preference } = useSelector((state: any) => state.layoutState);
  const { minMaxArea, minMaxDate } = useSelector(
    (state: any) => state.chartsState.localFilters
  );

  const nls = require(`./nls/${preference.language.id}.json`);

  const { realtyOffersFiltersOptionsGeneral, realtyOffersPrimaryGeneral } =
    useSelector((state: any) => state.offersPrimaryGeneralState);

  const { markers, clusters } = useSelector(
    (state: any) => state.dashboardState
  );

  const { bedrooms, suites, garages, inputIntervals } =
    realtyOffersPrimaryGeneral;

  const { realtyTypes } = realtyOffersFiltersOptionsGeneral;

  const [validation, setValidation]: any = useState({});
  const [avaliableInputIntervals, setAvaliableInputIntervals] =
    useState(inputIntervals);
  const [avaliableProgramFilters, setAvaliableProgramFilters] = useState({
    bedroom: true,
    suite: true,
    garage: true,
  });
  const [filtersInRegion, setFiltersInRegion] = useState<null>(null);
  const [realtyTypeFilterIsActive, setRealtyTypeFilterIsActive] =
    useState(true);
  const [filtersErrorMessage, setFiltersErrorMessage] = useState<null | string>(
    null
  );

  const inputPatterns: PrimaryMarketGeneralInputs = {
    privateAreaIntervalmin: {
      pattern: 'number',
      erro: validation,
      message:
        validation.privateAreaIntervalmin === 'biggerThanMax'
          ? nls.minBiggerThanMax
          : '',
    },
    privateAreaIntervalmax: {
      pattern: 'number',
      erro: validation,
      message:
        validation.privateAreaIntervalmax === 'smallerThanMin'
          ? nls.maxSmallerThanMin
          : '',
    },
    unitPricemin: {
      pattern: 'number',
      erro: validation,
      message:
        validation.unitPricemin === 'biggerThanMax' ? nls.minBiggerThanMax : '',
    },
    unitPricemax: {
      pattern: 'number',
      erro: validation,
      message:
        validation.unitPricemax === 'smallerThanMin'
          ? nls.maxSmallerThanMin
          : '',
    },
    pricePerSquareMtmin: {
      pattern: 'number',
      erro: validation,
      message:
        validation.pricePerSquareMtmin === 'biggerThanMax'
          ? nls.minBiggerThanMax
          : '',
    },
    pricePerSquareMtmax: {
      pattern: 'number',
      erro: validation,
      message:
        validation.pricePerSquareMtmax === 'smallerThanMin'
          ? nls.maxSmallerThanMin
          : '',
    },
  };

  const updateRealtyProgramFilters = (
    filterName: string,
    filterValue: boolean
  ) => {
    const updatedFilters = { ...avaliableProgramFilters };

    if (filterName === 'bedroom') {
      updatedFilters.bedroom = filterValue;
    } else if (filterName === 'garage') {
      updatedFilters.garage = filterValue;
    } else if (filterName === 'suite') {
      updatedFilters.suite = filterValue;
    }

    setAvaliableProgramFilters(updatedFilters);
  };

  const avaliableFiltersRules = (
    filtersData: Record<string, boolean> | null
  ) => {
    let inputIntervalsList = inputIntervals;
    let realtyProgramFilters = avaliableProgramFilters;

    if (filtersData) {
      Object.entries(filtersData)?.map((filter: [string, boolean]) => {
        const filterName: string = filter[0];
        const filterValue: boolean = filter[1];

        if (filterName === 'realtyType') {
          setRealtyTypeFilterIsActive(filterValue);
        }
        if (!filterValue) {
          switch (filterName) {
            case 'privateArea': {
              delete inputIntervalsList['privateAreaInterval'];
              break;
            }
            case 'squareMeterPrice': {
              delete inputIntervalsList['pricePerSquareMt'];
              break;
            }
            case 'price': {
              delete inputIntervalsList['unitPrice'];
              break;
            }
            case 'bedroom':
            case 'garage':
            case 'suite':
              updateRealtyProgramFilters(filterName, filterValue);
              break;
            default: {
              break;
            }
          }
        }

        setAvaliableInputIntervals(inputIntervalsList);
        setAvaliableProgramFilters(realtyProgramFilters);
      });
    }
  };

  const clearAvaliableFiltersState = () => {
    setAvaliableInputIntervals(false);
    setFiltersErrorMessage(null);
  };

  const getAvaliableFiltersByCity = (cityId: string, isUpdate?: boolean) => {
    clearAvaliableFiltersState();

    if (!isUpdate) {
      Promise.resolve(getAvaliableFilters(cityId))
        .then((response: AxiosResponse) => {
          const { data } = response;

          if (data) {
            avaliableFiltersRules(data);
            setFiltersInRegion(data);
          }
        })
        .catch((error) => {
          console.error(error);
          setFiltersErrorMessage(`${nls.error.avaliableFiltersError}`);
        });
    } else {
      avaliableFiltersRules(filtersInRegion);
    }
  };

  useEffect(() => {
    getAvaliableFiltersByCity(elastic_city_id);
  }, [elastic_city_id]);

  const handleClickMultiSelect = (
    e: any,
    array: Array<any>,
    arrayName: string
  ) => {
    const newArray = array.map((el) =>
      el.name === e.target.name ? { ...el, selected: !el.selected } : el
    );
    const payload = {
      name: arrayName,
      value: newArray,
    };
    dispatch(updateRealtyOffersPrimary(payload));
  };

  const handleClickSelect = (e: any, arrayName: string) => {
    const payload = {
      name: arrayName,
      value: e.value,
    };
    dispatch(updateRealtyOffersPrimary(payload));
  };

  const validateInputs = (
    event: any,
    v: { name: string; message: string },
    tuple: Array<any>,
    key: string
  ) => {
    const replaceCurrency = (el: string) => {
      if (el.includes(',')) return el.replace(',', '.');
      if (el.includes('R$')) {
        return parseInt(el.split('R$')[1].replaceAll('.', ''));
      } else {
        return parseInt(el);
      }
    };
    tuple[1] = {
      min: tuple[1].min.length > 0 ? replaceCurrency(tuple[1].min) : '',
      max: tuple[1].max.length > 0 ? replaceCurrency(tuple[1].max) : '',
    };
    if (Number(tuple[1].min) > Number(tuple[1].max)) {
      setValidation({ ...validation, [event.name]: v.message });
    } else if (!tuple[1].min && !tuple[1].max) {
      setValidation({
        ...validation,
        [`${key}min`]: false,
        [`${key}max`]: false,
      });
    } else {
      setValidation({
        ...validation,
        [`${key}min`]: false,
        [`${key}max`]: false,
      });
    }
  };

  const handleInput = (
    e: any,
    v: IResponse,
    tuple: { min: string; max: string }
  ) => {
    if (e.name.toLowerCase().includes('price')) {
      dispatch(handlePriceInputs(e, v, tuple, 'primary'));
    } else {
      dispatch(handleInputsIntervals(e, v, tuple, 'primary'));
    }
  };

  const handleClearFilters = () => {
    dispatch([
      setMinMaxAreaLocalFilters({ min: 0, max: 0 }),
      setMinMaxDateLocalFilters({ min: 0, max: 0 }),
      clearFiltersPrimary(),
    ]);
  };

  const handleSubmit = () => {
    const currentCityId = elastic_city_id;

    if (clusters.activeMap_ !== undefined) {
      markers.forEach((el: any) => {
        el.setMap(null);
      });
      clusters.clearMarkers();
      dispatch([updateMarkers([]), updateClusters([])]);
    }

    dispatch([
      setDisplayCharts(null),
      setMinUsefulArea(minMaxArea.min),
      setMaxUsefulArea(minMaxArea.max),
      setMinDate(minMaxDate.min),
      setMaxDate(minMaxDate.max),
      validateForm(
        realtyOffersPrimaryGeneral,
        validation,
        'primary',
        moreFilters,
        false,
        currentCityId
      ),
      closeModalAction?.(),
    ]);
  };

  return (
    <MenuWrapper
      title={nls.primaryMarketMenuTitle}
      market="Primary"
      submitButtonLabel={filtersErrorMessage ? nls.toSearch : nls.toApply}
      clearButtonLabel={nls.clearFilters}
      submitAction={handleSubmit}
      clearAction={handleClearFilters}
      isLoading={!avaliableInputIntervals}
      withoutCleanAction={filtersErrorMessage ? true : false}
    >
      <div className="realty-offers-primary-full-container">
        <div className="input-section-container">
          {avaliableInputIntervals ? (
            <>
              {realtyTypeFilterIsActive && (
                <div className="input-section-container">
                  <div className="input-section-container__title">
                    <span data-cy={'ContainerTitleRealtyType'}>
                      {nls.realtyType}
                    </span>
                    <span
                      data-cy={'ContainerTitleMandatoryRealtyType'}
                      className="optional-span"
                    >
                      {nls.mandatory}
                    </span>
                  </div>

                  <Select
                    label=""
                    cy="RealtyType"
                    options={realtyTypes}
                    action={(e: any) => handleClickSelect(e, 'realtyType')}
                    selected={realtyOffersPrimaryGeneral.realtyType || ''}
                    name="realtyType"
                  />
                </div>
              )}
              {Object.entries(avaliableInputIntervals)?.map(
                (input: any, index: number) => (
                  <div
                    className="timeframe-container row-container"
                    key={`input-section-container-${index}`}
                  >
                    <div className="input-section-container__title">
                      <span data-cy={`ContainerTitle${input[0]}`}>
                        {nls[input[0]]}
                      </span>
                    </div>

                    <div className="row-container inputs-container">
                      <div>
                        <Input
                          cy={`${input[0]}Min`}
                          action={(e, v) => handleInput(e, v, input[1])}
                          actionBlur={(e, v) =>
                            validateInputs(
                              e,
                              { ...v, message: 'biggerThanMax' },
                              input,
                              input[0]
                            )
                          }
                          name={`${input[0]}min`}
                          placeholder={nls.initial}
                          value={input[1]?.min}
                          required={
                            inputPatterns[
                              `${input[0]}min` as keyof PrimaryMarketGeneralInputs
                            ]
                          }
                          key={`${input[0]}Min`}
                        />
                      </div>
                      <div>
                        <Input
                          cy={`${input[0]}Max`}
                          action={(e, v) => handleInput(e, v, input[1])}
                          actionBlur={(e, v) =>
                            validateInputs(
                              e,
                              { ...v, message: 'smallerThanMin' },
                              input,
                              input[0]
                            )
                          }
                          name={`${input[0]}max`}
                          placeholder={nls.final}
                          value={input[1]?.max}
                          required={
                            inputPatterns[
                              `${input[0]}max` as keyof PrimaryMarketGeneralInputs
                            ]
                          }
                          key={`${input[0]}Max`}
                        />
                      </div>
                    </div>
                  </div>
                )
              )}
              <div className="input-section-container">
                <div className="buttons-container row-container">
                  <span data-cy="ContainerTitleBedrooms">{nls.bedrooms}</span>
                  <div className="multiselect-buttons-container row-container">
                    {bedrooms?.map((item: any) => (
                      <Button
                        className={`multi-select-button ${
                          item?.selected ? 'selected' : ''
                        }`}
                        cy={`BedroomsMultiSelect[${item?.name}]`}
                        name={item?.name}
                        key={item?.name}
                        onClick={(e) =>
                          handleClickMultiSelect(e, bedrooms, 'bedrooms')
                        }
                      >
                        {item?.name}
                      </Button>
                    ))}
                  </div>
                </div>
                <div className="buttons-container row-container">
                  <span data-cy="ContainerTitleSuites">{nls.suites}</span>
                  <div className="multiselect-buttons-container row-container">
                    {suites?.map((item: any) => (
                      <Button
                        className={`multi-select-button  ${
                          item?.selected ? 'selected' : ''
                        }`}
                        cy={`SuitesMultiSelect[${item?.name}]`}
                        name={item?.name}
                        key={item?.name}
                        onClick={(e) =>
                          handleClickMultiSelect(e, suites, 'suites')
                        }
                      >
                        {item?.name}
                      </Button>
                    ))}
                  </div>
                </div>
                <div className="buttons-container row-container">
                  <span data-cy="ContainerTitleGarages">{nls.garages}</span>
                  <div className="multiselect-buttons-container row-container">
                    {garages?.map((item: any) => (
                      <Button
                        className={`multi-select-button  ${
                          item?.selected ? 'selected' : ''
                        }`}
                        cy={`GaragesMultiSelect[${item?.name}]`}
                        name={item?.name}
                        key={item?.name}
                        onClick={(e) =>
                          handleClickMultiSelect(e, garages, 'garages')
                        }
                      >
                        {item?.name}
                      </Button>
                    ))}
                  </div>
                </div>
              </div>
            </>
          ) : (
            <>
              {filtersErrorMessage ? (
                <div className="filters-error">
                  <p className="filters-error__message">
                    {filtersErrorMessage}
                  </p>
                </div>
              ) : (
                <Spinner
                  withText
                  textValue={nls.loadingState.avaliableFilters}
                />
              )}
            </>
          )}
        </div>
      </div>
    </MenuWrapper>
  );
};

export default RealtyOffersPrimaryGeneral;
